import { EDatasourceType } from "@kuika/kuika-cl-model";
import type * as Monaco from "monaco-editor";
import {
  CodeAction,
  CompletionItemKind,
  LanguageServerService,
  LSPCompletionItem
} from "../../services/language-server-service";

/**
 * LSP-backed Monaco language features for SQL editors. The backend exposes a per-dialect SQL
 * language server (sqlserver / postgresql / oracle) mirroring the C# one; this module wires
 * Monaco's completion / hover / code-action / definition providers and diagnostics to it.
 *
 * Providers for the global `"sql"` language are registered exactly once and resolve the correct
 * datasource/dialect for whichever model is being queried via a per-model context registry. This
 * prevents the fanout that per-instance registration would cause: two open SQL editors, or an
 * unrelated read-only `"sql"` model (e.g. a code preview), never receive another editor's
 * datasource context or duplicate suggestions.
 */

/** A table from the active datasource's client-side schema, used to drive offline autocomplete. */
export interface SqlSchemaTable {
  name: string;
  schemaName?: string;
  columns: string[];
}

export interface SqlLspContext {
  getDatasourceId: () => string | null;
  getLanguageId: () => string;
  /**
   * Client-side schema (tables + their columns) for the active datasource. Drives deterministic
   * table/column autocomplete that does not depend on the backend SQL language server being
   * reachable — restoring the pre-LSP behavior (KOA-17735). Optional: callers without a local
   * schema fall back to LSP-only completion.
   */
  getSchema?: () => SqlSchemaTable[];
  /**
   * Lazily hydrate columns for the given tables. Schemas load via a slim path (KOA-17026) that
   * leaves a table's `columns` empty until a consumer asks for them; the completion provider calls
   * this for tables referenced in the query before suggesting their columns.
   */
  ensureColumns?: (tableNames: string[]) => Promise<void>;
  /** Datasource type, used for dialect-aware quoting of inserted identifiers. */
  getDatasourceType?: () => EDatasourceType | undefined;
}

/** Maps a datasource type to the backend SQL language-server id. */
export const toLspLanguageId = (datasourceType?: EDatasourceType): string => {
  if (datasourceType === EDatasourceType.Oracle || datasourceType === EDatasourceType.ManagedDbOracle) {
    return "oracle";
  }
  if (datasourceType === EDatasourceType.Postgre || datasourceType === EDatasourceType.ManagedDbPostgre) {
    return "postgresql";
  }
  return "sqlserver";
};

const FALLBACK_KEYWORDS: string[] = [
  "SELECT",
  "FROM",
  "WHERE",
  "GROUP BY",
  "ORDER BY",
  "INNER JOIN",
  "LEFT JOIN",
  "ON",
  "AND",
  "OR",
  "INSERT INTO",
  "VALUES",
  "UPDATE",
  "SET",
  "DELETE"
];

const mapKind = (monaco: typeof Monaco, kind: CompletionItemKind): Monaco.languages.CompletionItemKind => {
  switch (kind) {
    case CompletionItemKind.Class:
      return monaco.languages.CompletionItemKind.Class;
    case CompletionItemKind.Field:
      return monaco.languages.CompletionItemKind.Field;
    case CompletionItemKind.Function:
      return monaco.languages.CompletionItemKind.Function;
    case CompletionItemKind.Property:
      return monaco.languages.CompletionItemKind.Property;
    case CompletionItemKind.Keyword:
      return monaco.languages.CompletionItemKind.Keyword;
    default:
      return monaco.languages.CompletionItemKind.Text;
  }
};

const quoteIdentifier = (name: string): string => `"${name}"`;

/** Dialect-aware insert text for a table identifier (mirrors the pre-LSP getTableInsertText). */
const tableInsertText = (table: SqlSchemaTable, datasourceType?: EDatasourceType): string => {
  const name: string = table.name;
  const schemaName: string | undefined = table.schemaName;
  if (datasourceType === EDatasourceType.ManagedDb) {
    return schemaName ? `${schemaName}.${name}` : quoteIdentifier(name);
  }
  if (datasourceType === EDatasourceType.Oracle || datasourceType === EDatasourceType.ManagedDbOracle) {
    return quoteIdentifier(name);
  }
  if (datasourceType === EDatasourceType.Postgre || datasourceType === EDatasourceType.ManagedDbPostgre) {
    return schemaName ? `${quoteIdentifier(schemaName)}.${quoteIdentifier(name)}` : quoteIdentifier(name);
  }
  return schemaName ? `${schemaName}.${name}` : name;
};

/** Dialect-aware insert text for a column identifier (mirrors the pre-LSP getColumnInsertText). */
const columnInsertText = (columnName: string, table: SqlSchemaTable, datasourceType?: EDatasourceType): string => {
  if (datasourceType === EDatasourceType.ManagedDb) {
    return table.schemaName ? columnName : quoteIdentifier(columnName);
  }
  if (
    datasourceType === EDatasourceType.Oracle ||
    datasourceType === EDatasourceType.Postgre ||
    datasourceType === EDatasourceType.ManagedDbPostgre
  ) {
    return quoteIdentifier(columnName);
  }
  return columnName;
};

/**
 * Builds deterministic completion items from the client-side schema: every table (sorted first),
 * plus the columns of any table referenced in the current query text (sorted next). Columns are
 * scoped to referenced tables to mirror the pre-LSP behavior and avoid flooding the list.
 */
const buildSchemaSuggestions = (
  monaco: typeof Monaco,
  tables: SqlSchemaTable[],
  code: string,
  datasourceType: EDatasourceType | undefined,
  range: Monaco.IRange
): Monaco.languages.CompletionItem[] => {
  const suggestions: Monaco.languages.CompletionItem[] = [];
  const lowerCode: string = code.toLowerCase();
  for (const table of tables) {
    suggestions.push({
      label: table.name,
      kind: monaco.languages.CompletionItemKind.Class,
      insertText: tableInsertText(table, datasourceType),
      range,
      filterText: table.name,
      sortText: `0_${table.name}`
    });
    const isReferenced: boolean = lowerCode.includes(table.name.toLowerCase());
    if (!isReferenced) continue;
    for (const column of table.columns) {
      suggestions.push({
        label: column,
        kind: monaco.languages.CompletionItemKind.Field,
        insertText: columnInsertText(column, table, datasourceType),
        range,
        filterText: column,
        sortText: `1_${column}`
      });
    }
  }
  return suggestions;
};

/** Static SQL keyword completions, always offered so the editor never feels dead. */
const buildKeywordSuggestions = (monaco: typeof Monaco, range: Monaco.IRange): Monaco.languages.CompletionItem[] =>
  FALLBACK_KEYWORDS.map((kw: string): Monaco.languages.CompletionItem => ({
    label: kw,
    kind: monaco.languages.CompletionItemKind.Keyword,
    insertText: kw,
    range,
    filterText: kw,
    sortText: `2_${kw}`
  }));

/** Extracts the plain-text label of a completion item for case-insensitive de-duplication. */
const labelKey = (item: Monaco.languages.CompletionItem): string =>
  (typeof item.label === "string" ? item.label : item.label.label).toLowerCase();

/**
 * Assembles the final SQL completion list: deterministic client-side schema (tables + referenced
 * columns) and SQL keywords first, then any backend LSP items whose label is not already present.
 * Client items win on label collisions because their dialect-aware insert text is authoritative for
 * the datasource. Pure (no I/O) so it can be unit-tested directly.
 */
export const buildSqlCompletionList = (
  monaco: typeof Monaco,
  tables: SqlSchemaTable[],
  code: string,
  datasourceType: EDatasourceType | undefined,
  lspItems: LSPCompletionItem[],
  range: Monaco.IRange
): Monaco.languages.CompletionList => {
  const clientSuggestions: Monaco.languages.CompletionItem[] = [
    ...buildSchemaSuggestions(monaco, tables, code, datasourceType, range),
    ...buildKeywordSuggestions(monaco, range)
  ];

  const lspSuggestions: Monaco.languages.CompletionItem[] = lspItems.map(
    (item: LSPCompletionItem): Monaco.languages.CompletionItem => ({
      label: item.label,
      kind: mapKind(monaco, item.kind),
      insertText: item.insertText,
      detail: item.detail,
      range,
      filterText: item.label,
      sortText: `3_${item.label}`
    })
  );

  const seen: Set<string> = new Set<string>();
  const suggestions: Monaco.languages.CompletionItem[] = [];
  for (const item of [...clientSuggestions, ...lspSuggestions]) {
    const key: string = labelKey(item);
    if (seen.has(key)) continue;
    seen.add(key);
    suggestions.push(item);
  }

  // LSP results are prefix-filtered and capped server-side, so when any are present ask Monaco to
  // re-query as the prefix narrows instead of filtering a stale, truncated set client-side.
  return { incomplete: lspItems.length > 0, suggestions };
};

const mapSeverity = (monaco: typeof Monaco, severity: number): Monaco.MarkerSeverity => {
  if (severity >= 3) return monaco.MarkerSeverity.Error;
  if (severity === 2) return monaco.MarkerSeverity.Warning;
  if (severity === 1) return monaco.MarkerSeverity.Info;
  return monaco.MarkerSeverity.Hint;
};

const DIAGNOSTICS_OWNER = "kuika-sql";
const diagnosticsTimers: Map<string, ReturnType<typeof setTimeout>> = new Map();

// Per-model SQL editor context, keyed by model URI. A model with no entry is not a Kuika SQL
// editor, so the shared providers stay silent for it.
const modelContexts: Map<string, SqlLspContext> = new Map();
let providersRegistered: boolean = false;

const contextFor = (model: Monaco.editor.ITextModel): SqlLspContext | undefined =>
  modelContexts.get(model.uri.toString());

/** Registers the shared `"sql"` providers once for the app lifetime. */
const ensureProvidersRegistered = (monaco: typeof Monaco): void => {
  if (providersRegistered) return;
  providersRegistered = true;

  monaco.languages.registerCompletionItemProvider("sql", {
    triggerCharacters: ["."],
    provideCompletionItems: async (
      model: Monaco.editor.ITextModel,
      position: Monaco.Position
    ): Promise<Monaco.languages.CompletionList> => {
      const ctx = contextFor(model);
      if (!ctx) return { suggestions: [] };

      const word = model.getWordUntilPosition(position);
      const range: Monaco.IRange = {
        startLineNumber: position.lineNumber,
        endLineNumber: position.lineNumber,
        startColumn: word.startColumn,
        endColumn: word.endColumn
      };

      const code: string = model.getValue();
      const datasourceType: EDatasourceType | undefined = ctx.getDatasourceType?.();

      // Fire the LSP request and the column hydration concurrently — neither blocks the other.
      const lspPromise: Promise<LSPCompletionItem[]> = LanguageServerService.GetCompletions(
        code,
        model.getOffsetAt(position),
        ctx.getDatasourceId(),
        ctx.getLanguageId()
      );

      // Hydrate columns for referenced tables (KOA-17026 slim path) before reading the schema.
      const initialTables: SqlSchemaTable[] = ctx.getSchema?.() ?? [];
      if (ctx.ensureColumns && initialTables.length) {
        const lowerCode: string = code.toLowerCase();
        const referenced: string[] = initialTables
          .filter((table: SqlSchemaTable): boolean => lowerCode.includes(table.name.toLowerCase()))
          .map((table: SqlSchemaTable): string => table.name);
        if (referenced.length) {
          try {
            await ctx.ensureColumns(referenced);
          } catch {
            // Best-effort: fall back to whatever columns are already loaded.
          }
        }
      }
      const tables: SqlSchemaTable[] = ctx.getSchema?.() ?? initialTables;

      const lspItems: LSPCompletionItem[] = await lspPromise;
      return buildSqlCompletionList(monaco, tables, code, datasourceType, lspItems, range);
    }
  });

  monaco.languages.registerHoverProvider("sql", {
    provideHover: async (
      model: Monaco.editor.ITextModel,
      position: Monaco.Position
    ): Promise<Monaco.languages.Hover | null> => {
      const ctx = contextFor(model);
      if (!ctx) return null;
      const info = await LanguageServerService.GetHoverInfo(
        model.getValue(),
        model.getOffsetAt(position),
        ctx.getDatasourceId(),
        ctx.getLanguageId()
      );
      if (!info) return null;
      return {
        range: {
          startLineNumber: info.startLineNumber + 1,
          startColumn: info.startColumn + 1,
          endLineNumber: info.endLineNumber + 1,
          endColumn: info.endColumn + 1
        },
        contents: [{ value: info.content, isTrusted: true, supportThemeIcons: true }]
      };
    }
  });

  monaco.languages.registerCodeActionProvider("sql", {
    provideCodeActions: async (model: Monaco.editor.ITextModel): Promise<Monaco.languages.CodeActionList> => {
      const ctx = contextFor(model);
      if (!ctx) return { actions: [], dispose: () => {} };

      const actions: CodeAction[] = await LanguageServerService.GetCodeActions(
        model.getValue(),
        ctx.getDatasourceId(),
        undefined,
        undefined,
        ctx.getLanguageId()
      );

      const fullRange: Monaco.IRange = model.getFullModelRange();
      return {
        actions: actions
          .filter((action: CodeAction) => Boolean(action.editedCode))
          .map((action: CodeAction): Monaco.languages.CodeAction => ({
            title: action.title,
            kind: "quickfix",
            edit: {
              edits: [
                {
                  resource: model.uri,
                  textEdit: { range: fullRange, text: action.editedCode },
                  versionId: undefined
                }
              ]
            }
          })),
        dispose: () => {}
      };
    }
  });

  monaco.languages.registerDefinitionProvider("sql", {
    provideDefinition: async (
      model: Monaco.editor.ITextModel,
      position: Monaco.Position
    ): Promise<Monaco.languages.Definition | null> => {
      const ctx = contextFor(model);
      if (!ctx) return null;
      const def = await LanguageServerService.GetDefinition(
        model.getValue(),
        model.getOffsetAt(position),
        ctx.getDatasourceId(),
        ctx.getLanguageId()
      );
      if (!def) return null;
      return {
        uri: model.uri,
        range: {
          startLineNumber: def.startLineNumber + 1,
          startColumn: def.startColumn + 1,
          endLineNumber: def.endLineNumber + 1,
          endColumn: def.endColumn + 1
        }
      };
    }
  });
};

/**
 * Binds an editor model to a SQL LSP context so the shared providers resolve the right
 * datasource/dialect for it, and runs an initial diagnostics pass. Returns a disposable that
 * detaches the model (call it on editor unmount). Takes the model rather than the editor so the
 * type stays compatible across the (possibly duplicated) monaco-editor copies in node_modules.
 */
export const attachSqlEditor = (
  monaco: typeof Monaco,
  model: Monaco.editor.ITextModel | null | undefined,
  ctx: SqlLspContext
): Monaco.IDisposable => {
  ensureProvidersRegistered(monaco);

  const key = model ? model.uri.toString() : null;
  if (model && key) {
    modelContexts.set(key, ctx);
    updateSqlDiagnostics(monaco, model, model.getValue(), ctx);
  }

  return {
    dispose: () => {
      if (!key) return;
      modelContexts.delete(key);
      const timer = diagnosticsTimers.get(key);
      if (timer) {
        clearTimeout(timer);
        diagnosticsTimers.delete(key);
      }
    }
  };
};

/** Debounced syntax + semantic diagnostics for a SQL model, drawn as Monaco markers. */
export const updateSqlDiagnostics = (
  monaco: typeof Monaco | undefined,
  model: Monaco.editor.ITextModel | null | undefined,
  code: string | undefined,
  ctx: SqlLspContext
): void => {
  if (!monaco || !model || model.isDisposed()) return;

  const key = model.uri.toString();
  const existing = diagnosticsTimers.get(key);
  if (existing) clearTimeout(existing);

  const timer = setTimeout(async () => {
    diagnosticsTimers.delete(key);
    if (model.isDisposed()) return;

    const diagnostics = await LanguageServerService.GetDiagnostics(
      code ?? model.getValue(),
      ctx.getDatasourceId(),
      undefined,
      ctx.getLanguageId()
    );

    if (model.isDisposed()) return;
    const markers: Monaco.editor.IMarkerData[] = diagnostics.map((d) => ({
      severity: mapSeverity(monaco, d.severity),
      message: d.message,
      startLineNumber: d.startLineNumber + 1,
      startColumn: d.startColumn + 1,
      endLineNumber: d.endLineNumber + 1,
      endColumn: d.endColumn + 1
    }));
    monaco.editor.setModelMarkers(model, DIAGNOSTICS_OWNER, markers);
  }, 400);

  diagnosticsTimers.set(key, timer);
};
